import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Star, Sparkles, Shield, Truck, Headphones } from "lucide-react";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import ParticleField from "@/components/ParticleField";
import { HOME } from "@/constants/testIds";

const HERO_IMG = "https://images.pexels.com/photos/7989742/pexels-photo-7989742.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const MOBILE_IMG = "https://images.unsplash.com/photo-1744591433649-28069739f80b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwzfHxwcmVtaXVtJTIwZWFyYnVkcyUyMGRhcmt8ZW58MHx8fHwxNzgzNzQwNzEyfDA&ixlib=rb-4.1.0&q=85";
const LIFESTYLE_IMG = "https://images.pexels.com/photos/9430461/pexels-photo-9430461.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const revealUp = {
  hidden: { opacity: 0, y: 32 },
  show: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: i * 0.08 },
  }),
};

function SectionHeading({ eyebrow, title, action }) {
  return (
    <motion.div
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, amount: 0.4 }}
      variants={revealUp}
      className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-14"
    >
      <div>
        <div className="text-[10px] font-mono uppercase tracking-[0.32em] text-electric">{eyebrow}</div>
        <h2 className="mt-4 font-heading font-medium text-4xl sm:text-5xl md:text-6xl tracking-[-0.03em] text-balance max-w-2xl leading-[1.05]">{title}</h2>
      </div>
      {action}
    </motion.div>
  );
}


export default function HomePage() {
  const [best, setBest] = useState([]);
  const [newest, setNewest] = useState([]);
  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    api.get("/products", { params: { best_seller: true, limit: 8 } }).then((r) => setBest(r.data)).catch(() => {});
    api.get("/products", { params: { new_arrival: true, limit: 8 } }).then((r) => setNewest(r.data)).catch(() => {});
    api.get("/reviews", { params: { limit: 6 } }).then((r) => setReviews(r.data)).catch(() => {});
  }, []);

  return (
    <div className="relative">
      {/* HERO */}
      <section className="relative overflow-hidden bg-[#0B0B0B] text-white -mt-20 pt-20">
        {/* Ambient blue orbs */}
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none">
          <div className="absolute top-[-20%] left-[10%] w-[45rem] h-[45rem] rounded-full blur-[140px] opacity-40"
               style={{ background: "radial-gradient(circle, #2563EB 0%, transparent 60%)" }} />
          <div className="absolute bottom-[-25%] right-[-10%] w-[40rem] h-[40rem] rounded-full blur-[140px] opacity-30"
               style={{ background: "radial-gradient(circle, #2563EB 0%, transparent 60%)" }} />
        </div>

        {/* Particle field */}
        <ParticleField />

        {/* Grid overlay */}
        <div aria-hidden="true" className="absolute inset-0 opacity-[0.06]"
             style={{ backgroundImage: "linear-gradient(rgba(255,255,255,.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.6) 1px, transparent 1px)", backgroundSize: "56px 56px", maskImage: "radial-gradient(ellipse 60% 60% at 50% 50%, black 40%, transparent 100%)" }} />

        <div className="relative max-w-7xl mx-auto px-6 md:px-10 min-h-[100svh] flex flex-col items-center justify-center text-center py-32">
          {/* Kicker */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.3em] text-white/60 border border-white/15 rounded-full px-3 py-1.5 bg-white/[0.03] backdrop-blur-sm"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-[#2563EB] animate-pulse" /> SSR Innovations · Winter '26
          </motion.div>

          {/* Glowing wordmark */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.1, ease: [0.19, 1, 0.22, 1] }}
            className="relative mt-10 select-none"
            data-testid="hero-logo"
          >
            <div
              className="font-heading font-medium tracking-[-0.06em] leading-none text-glow-electric"
              style={{
                fontSize: "clamp(6rem, 22vw, 20rem)",
                background: "linear-gradient(180deg, #FFFFFF 0%, #FFFFFF 55%, rgba(37,99,235,0.75) 100%)",
                WebkitBackgroundClip: "text",
                backgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              SSR
            </div>
            {/* Reflection */}
            <div
              aria-hidden="true"
              className="absolute left-0 right-0 top-full -mt-2 opacity-20 blur-[2px] scale-y-[-1]"
              style={{
                fontSize: "clamp(6rem, 22vw, 20rem)",
                fontWeight: 500,
                letterSpacing: "-0.06em",
                lineHeight: 1,
                fontFamily: '"Cabinet Grotesk", "Space Grotesk", sans-serif',
                background: "linear-gradient(180deg, rgba(37,99,235,0.6) 0%, transparent 60%)",
                WebkitBackgroundClip: "text",
                backgroundClip: "text",
                WebkitTextFillColor: "transparent",
                maskImage: "linear-gradient(180deg, black 0%, transparent 70%)",
                WebkitMaskImage: "linear-gradient(180deg, black 0%, transparent 70%)",
              }}
            >
              SSR
            </div>
          </motion.div>

          {/* Headline */}
          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.35 }}
            className="mt-6 font-heading font-medium tracking-tight text-3xl sm:text-5xl md:text-6xl text-balance max-w-4xl"
          >
            Technology <span className="italic font-light text-[#2563EB]">Meets</span> Lifestyle
          </motion.h1>

          {/* Subheading */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="mt-6 max-w-xl text-base md:text-lg text-white/60 leading-relaxed text-balance"
          >
            Premium mobile accessories and lifestyle essentials designed for modern creators.
          </motion.p>

          {/* Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.65 }}
            className="mt-10 flex flex-wrap items-center justify-center gap-3"
          >
            <Link
              to="/shop"
              data-testid={HOME.heroCta}
              className="group inline-flex items-center gap-2 h-12 pl-6 pr-4 rounded-full bg-[#2563EB] text-white text-sm font-medium ring-glow-electric hover:brightness-110 hover:-translate-y-[1px] transition-all duration-300"
            >
              Shop Now
              <span className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center group-hover:translate-x-0.5 transition-transform">
                <ArrowRight className="w-4 h-4" />
              </span>
            </Link>
            <Link
              to="/lifestyle"
              data-testid={HOME.heroExploreCta}
              className="inline-flex items-center gap-2 h-12 px-6 rounded-full border border-white/20 text-white text-sm font-medium bg-white/[0.02] backdrop-blur-sm hover:border-[#2563EB]/70 hover:bg-white/[0.06] hover:-translate-y-[1px] transition-all duration-300"
            >
              Explore Collection
            </Link>
          </motion.div>

          {/* Scroll indicator */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.2, duration: 0.8 }}
            className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-white/40"
            aria-hidden="true"
          >
            <div className="text-[10px] font-mono uppercase tracking-[0.3em]">Scroll</div>
            <motion.div
              animate={{ y: [0, 8, 0] }}
              transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
              className="w-px h-8 bg-gradient-to-b from-white/60 to-transparent"
            />
          </motion.div>
        </div>

        {/* Fade to page bg */}
        <div aria-hidden="true" className="absolute bottom-0 inset-x-0 h-32 bg-gradient-to-b from-transparent to-background" />
      </section>

      {/* Marquee */}
      <section className="border-y border-white/5 bg-black/40 py-5 overflow-hidden relative">
        <div className="absolute inset-0 pointer-events-none" style={{ background: "linear-gradient(90deg, #0B0B0B 0%, transparent 8%, transparent 92%, #0B0B0B 100%)" }} />
        <div className="flex gap-16 animate-marquee whitespace-nowrap font-mono text-[11px] uppercase tracking-[0.35em] text-white/50">
          {Array.from({ length: 2 }).map((_, r) => (
            <div key={r} className="flex gap-16 shrink-0">
              <span>Free shipping over ₹999</span><span className="text-electric">·</span>
              <span>2-year warranty</span><span className="text-electric">·</span>
              <span>Made for iPhone · Android</span><span className="text-electric">·</span>
              <span>Premium materials</span><span className="text-electric">·</span>
              <span>Designed in India</span><span className="text-electric">·</span>
              <span>Delivered worldwide</span><span className="text-electric">·</span>
            </div>
          ))}
        </div>
      </section>

      {/* Featured Categories */}
      <section className="relative max-w-7xl mx-auto px-6 md:px-10 py-32 md:py-40">
        <SectionHeading
          eyebrow="01 — Collections"
          title={<>Two worlds. <span className="text-muted-foreground">One brand.</span></>}
          action={<Link to="/shop" className="hidden md:inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-electric transition-colors group">All products <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" /></Link>}
        />
        <div className="grid md:grid-cols-2 gap-4 md:gap-6">
          {[
            { to: "/mobile-accessories", id: HOME.featuredMobile, img: MOBILE_IMG, num: "Category 01", title: "Mobile Accessories", cta: "Shop the collection" },
            { to: "/lifestyle", id: HOME.featuredLifestyle, img: LIFESTYLE_IMG, num: "Category 02", title: "Lifestyle", cta: "Explore the range" },
          ].map((c, i) => (
            <motion.div
              key={c.to}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.2 }}
              variants={revealUp}
              custom={i}
            >
              <Link data-testid={c.id} to={c.to} className="group relative block aspect-[4/5] md:aspect-[16/12] rounded-3xl overflow-hidden bg-secondary border border-white/5">
                <img src={c.img} alt={c.title} className="w-full h-full object-cover transition-transform duration-[1.4s] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.08]" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent" />
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                  style={{ background: "radial-gradient(600px circle at 30% 100%, rgba(37,99,235,0.28), transparent 60%)" }} />
                <div className="absolute bottom-0 left-0 p-8 md:p-10">
                  <div className="text-[10px] font-mono uppercase tracking-[0.32em] text-white/60">{c.num}</div>
                  <div className="mt-3 text-white font-heading font-medium text-4xl md:text-5xl tracking-[-0.02em]">{c.title}</div>
                  <div className="mt-4 inline-flex items-center gap-1.5 text-white/90 text-sm group-hover:text-electric transition-colors">
                    {c.cta}
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Best Sellers */}
      {best.length > 0 && (
        <section className="relative max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32">
          <SectionHeading
            eyebrow="02 — Bestsellers"
            title={<>What everyone's <span className="italic font-light text-electric">buying</span></>}
            action={<Link to="/shop?best_seller=1" className="hidden md:inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-electric transition-colors group">View all <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" /></Link>}
          />
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.1 }}
            className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6"
          >
            {best.map((p, i) => (
              <motion.div key={p.id} variants={revealUp} custom={i}><ProductCard product={p} /></motion.div>
            ))}
          </motion.div>
        </section>
      )}

      {/* Ambient divider */}
      <div aria-hidden="true" className="relative h-40 overflow-hidden">
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[80rem] h-[16rem] rounded-full blur-[120px] opacity-30"
             style={{ background: "radial-gradient(ellipse, rgba(37,99,235,0.9) 0%, transparent 60%)" }} />
      </div>

      {/* New Arrivals */}
      {newest.length > 0 && (
        <section className="relative max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32">
          <SectionHeading
            eyebrow="03 — Just landed"
            title={<>New arrivals.<br /><span className="text-muted-foreground">Freshly engineered.</span></>}
            action={<Link to="/shop?new=1" className="hidden md:inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-electric transition-colors group">View all <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" /></Link>}
          />
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.1 }}
            className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6"
          >
            {newest.map((p, i) => (
              <motion.div key={p.id} variants={revealUp} custom={i}><ProductCard product={p} /></motion.div>
            ))}
          </motion.div>
        </section>
      )}

      {/* Value props */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32">
        <SectionHeading
          eyebrow="04 — Why choose SSR"
          title={<>Why choose <span className="italic font-light text-electric">SSR.</span></>}
        />
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.2 }}
          className="grid md:grid-cols-4 gap-4 md:gap-5"
        >
          {[
            { icon: Sparkles, title: "Premium materials", body: "Aerospace-grade aluminum, vegan leather, tempered glass." },
            { icon: Shield, title: "2-year warranty", body: "Every product backed by SSR's lifetime promise." },
            { icon: Truck, title: "Free shipping", body: "Complimentary shipping on orders above ₹999." },
            { icon: Headphones, title: "24/7 support", body: "Real humans, always ready to help." },
          ].map((v, i) => (
            <motion.div
              key={i}
              variants={revealUp}
              custom={i}
              className="group relative p-8 rounded-2xl border border-border bg-card overflow-hidden transition-colors duration-500 hover:border-electric/50"
            >
              <div className="absolute -inset-px opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-2xl pointer-events-none"
                style={{ background: "radial-gradient(400px circle at 50% 0%, rgba(37,99,235,0.15), transparent 70%)" }} />
              <v.icon className="relative w-5 h-5 text-electric transition-transform duration-500 group-hover:scale-110" />
              <div className="relative mt-6 font-heading font-medium text-xl tracking-tight">{v.title}</div>
              <div className="relative mt-2 text-sm text-muted-foreground leading-relaxed">{v.body}</div>
            </motion.div>
          ))}
        </motion.div>
      </section>

      {/* Reviews */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32">
        <SectionHeading
          eyebrow="05 — Trusted by 40,000+"
          title={<>Loved by <span className="italic font-light text-electric">people</span> who notice details.</>}
        />
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.1 }}
          className="grid md:grid-cols-3 gap-4 md:gap-6"
        >
          {(reviews.length > 0 ? reviews : demoReviews).slice(0, 6).map((r, i) => (
            <motion.div
              key={r.id || i}
              variants={revealUp}
              custom={i}
              className="group p-8 rounded-2xl border border-border bg-card hover:border-electric/40 transition-colors duration-500"
            >
              <div className="flex gap-0.5 text-electric">
                {Array.from({ length: r.rating || 5 }).map((_, j) => <Star key={j} className="w-3.5 h-3.5 fill-current" />)}
              </div>
              <div className="mt-5 font-heading font-medium text-lg tracking-tight">{r.title}</div>
              <div className="mt-3 text-sm text-muted-foreground leading-relaxed">{r.body}</div>
              <div className="mt-6 text-[10px] font-mono uppercase tracking-[0.28em] text-muted-foreground">— {r.user_name}</div>
            </motion.div>
          ))}
        </motion.div>
      </section>

      {/* Newsletter */}
      <NewsletterSection />

      {/* Closing CTA */}
      <section className="relative py-32 md:py-48 overflow-hidden">
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none">
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[60rem] h-[60rem] rounded-full blur-[160px] opacity-25"
               style={{ background: "radial-gradient(circle, #2563EB 0%, transparent 60%)" }} />
        </div>
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="relative max-w-4xl mx-auto px-6 text-center"
        >
          <div className="text-[10px] font-mono uppercase tracking-[0.32em] text-electric">Ready when you are</div>
          <h2 className="mt-6 font-heading font-medium text-5xl sm:text-6xl md:text-7xl tracking-[-0.03em] leading-[1.02] text-balance">
            Own the details.<br />
            <span className="italic font-light">Elevate</span> the everyday.
          </h2>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
            <Link
              to="/shop"
              className="group inline-flex items-center gap-2 h-12 pl-6 pr-4 rounded-full bg-electric text-white text-sm font-medium ring-glow-electric hover:brightness-110 hover:-translate-y-[1px] transition-all duration-300"
            >
              Shop Now
              <span className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center group-hover:translate-x-0.5 transition-transform">
                <ArrowRight className="w-4 h-4" />
              </span>
            </Link>
            <Link
              to="/lifestyle"
              className="inline-flex items-center gap-2 h-12 px-6 rounded-full border border-border hover:border-electric/70 text-sm font-medium hover:-translate-y-[1px] transition-all duration-300"
            >
              Explore Collection
            </Link>
          </div>
        </motion.div>
      </section>
    </div>
  );
}

const demoReviews = [
  { title: "Just perfect.", body: "The magnetic case fits like a glove and the finish feels premium. This is now my daily driver.", user_name: "Aarav K.", rating: 5 },
  { title: "Sound is unreal.", body: "SSR Earbuds Pro sound better than gear costing 3x. Buy them.", user_name: "Neha S.", rating: 5 },
  { title: "Elegant and simple.", body: "The ring set arrived beautifully packaged. Wear them every day.", user_name: "Rahul M.", rating: 5 },
];

function NewsletterSection() {
  const [email, setEmail] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [sent, setSent] = React.useState(false);

  async function subscribe(e) {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await api.post("/newsletter", { email });
      setSent(true);
      setEmail("");
    } catch { /* silent */ }
    finally { setLoading(false); }
  }

  return (
    <section className="relative py-24 md:py-32 overflow-hidden">
      <div aria-hidden="true" className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[45rem] h-[45rem] rounded-full blur-[140px] opacity-25"
             style={{ background: "radial-gradient(circle, #2563EB 0%, transparent 60%)" }} />
      </div>
      <motion.div
        initial={{ opacity: 0, y: 32 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        className="relative max-w-4xl mx-auto px-6"
      >
        <div className="relative rounded-3xl border border-white/10 bg-white/[0.03] backdrop-blur-xl p-10 md:p-16 overflow-hidden">
          <div aria-hidden="true" className="absolute inset-0 opacity-[0.06] pointer-events-none"
               style={{ backgroundImage: "linear-gradient(rgba(255,255,255,.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.6) 1px, transparent 1px)", backgroundSize: "40px 40px", maskImage: "radial-gradient(ellipse 70% 60% at 50% 50%, black 40%, transparent 100%)" }} />
          <div className="relative text-center">
            <div className="text-[10px] font-mono uppercase tracking-[0.32em] text-electric">06 — Newsletter</div>
            <h2 className="mt-5 font-heading font-medium text-4xl sm:text-5xl md:text-6xl tracking-[-0.03em] leading-[1.05] text-balance">
              Join the <span className="italic font-light text-electric">inside</span> line.
            </h2>
            <p className="mt-5 max-w-lg mx-auto text-muted-foreground leading-relaxed">
              Early access to drops, subscriber-only pricing, and product previews before anyone else.
            </p>
            {sent ? (
              <div data-testid="newsletter-success" className="mt-8 inline-flex items-center gap-2 h-12 px-6 rounded-full border border-electric/50 bg-electric/10 text-electric text-sm font-medium">
                <span className="w-1.5 h-1.5 rounded-full bg-electric animate-pulse" />
                You're on the list. Welcome.
              </div>
            ) : (
              <form onSubmit={subscribe} className="mt-8 flex flex-col sm:flex-row items-stretch gap-2 max-w-md mx-auto">
                <input
                  data-testid="home-newsletter-input"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="flex-1 h-12 px-5 rounded-full bg-white/[0.05] border border-white/10 text-sm placeholder:text-muted-foreground outline-none focus:border-electric/50 focus:bg-white/[0.08] transition-colors"
                />
                <button
                  data-testid="home-newsletter-submit"
                  disabled={loading}
                  className="h-12 px-6 rounded-full bg-electric text-white text-sm font-medium ring-glow-electric hover:brightness-110 hover:-translate-y-[1px] disabled:opacity-60 transition-all duration-300 whitespace-nowrap"
                >
                  {loading ? "Joining…" : "Subscribe"}
                </button>
              </form>
            )}
            <div className="mt-6 text-[10px] font-mono uppercase tracking-[0.28em] text-muted-foreground">
              No spam. Unsubscribe anytime.
            </div>
          </div>
        </div>
      </motion.div>
    </section>
  );
}
